import { useState, useRef } from "react";
import { useLocation } from "react-router-dom";
import { Menu, Moon, Sun, LogOut, User, Home, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { useThemeMode } from "@/theme/ThemeContext";
import { useAuth } from "@/context/AuthContext";
import ProfileDialog from "@/components/common/ProfileDialog";
import LogoutDialog from "@/components/common/LogoutDialog";
import { SIDEBAR_WIDTH } from "./Sidebar";

const segmentLabels: Record<string, string> = {
  transactions: "Transactions",
  wallet: "Wallet",
  invoices: "Invoices",
  credit: "Credit",
  "discount-mapping": "Discount Mapping",
  "gift-cards": "Gift Cards",
  bbps: "BBPS",
  "shared-subscriptions": "Shared Subscriptions",
  "order-history": "Order History",
  stocks: "Stocks",
  settings: "Settings",
  general: "General",
  developer: "Developer",
};

function formatSegment(segment: string): string {
  return segmentLabels[segment] ?? segment.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

interface TopBarProps {
  onMenuClick: () => void;
  isMobile: boolean;
}

export default function TopBar({ onMenuClick, isMobile }: TopBarProps) {
  const { mode, toggleTheme } = useThemeMode();
  const { user, logout } = useAuth();
  const location = useLocation();
  const [profileOpen, setProfileOpen] = useState(false);
  const [logoutOpen, setLogoutOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const displayName = user?.fullname || "User";
  const initials = displayName
    .split(" ")
    .map((n) => n[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  const pathSegments = location.pathname.split("/").filter(Boolean);

  const handleMouseEnter = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setMenuOpen(true);
  };

  const handleMouseLeave = () => {
    closeTimer.current = setTimeout(() => setMenuOpen(false), 150);
  };

  return (
    <header
      className="fixed top-0 z-50 h-16 border-b border-border bg-card/80 backdrop-blur-sm"
      style={{
        width: isMobile ? "100%" : `calc(100% - ${SIDEBAR_WIDTH}px)`,
        marginLeft: isMobile ? 0 : `${SIDEBAR_WIDTH}px`,
      }}
    >
      <div className="flex items-center h-full px-6">
        {isMobile && (
          <Button variant="ghost" size="icon" onClick={onMenuClick} className="mr-2">
            <Menu className="h-5 w-5" />
          </Button>
        )}

        {/* Breadcrumbs */}
        <nav className="flex items-center gap-1.5 text-sm">
          <a href="/" className="text-muted-foreground hover:text-foreground transition-colors">
            <Home className="h-4 w-4" />
          </a>
          {pathSegments.map((segment, index) => {
            const path = "/" + pathSegments.slice(0, index + 1).join("/");
            const isLast = index === pathSegments.length - 1;
            return (
              <span key={path} className="flex items-center gap-1.5">
                <ChevronRight className="h-3.5 w-3.5 text-muted-foreground/50" />
                {isLast ? (
                  <span className="font-medium text-foreground">{formatSegment(segment)}</span>
                ) : (
                  <a href={path} className="text-muted-foreground hover:text-foreground transition-colors">
                    {formatSegment(segment)}
                  </a>
                )}
              </span>
            );
          })}
        </nav>

        <div className="flex-1" />

        {/* Actions */}
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="icon" onClick={toggleTheme} className="text-muted-foreground">
            {mode === "dark" ? <Sun className="h-[18px] w-[18px]" /> : <Moon className="h-[18px] w-[18px]" />}
          </Button>

          {/* User menu — opens on hover */}
          <div
            className="relative ml-2"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            <button className="flex items-center gap-2.5 cursor-pointer rounded-lg px-2 py-1.5 hover:bg-accent transition-colors">
              <Avatar className="h-8 w-8">
                <AvatarImage src={user?.dp || undefined} />
                <AvatarFallback className="bg-primary text-primary-foreground text-xs font-medium">
                  {initials}
                </AvatarFallback>
              </Avatar>
              {!isMobile && (
                <span className="text-sm font-medium">{displayName}</span>
              )}
            </button>

            {menuOpen && (
              <div className="absolute right-0 top-full mt-1 w-[200px] rounded-lg border border-border bg-popover p-1 text-popover-foreground shadow-lg animate-in fade-in-0 zoom-in-95 duration-100">
                {user && (
                  <>
                    <div className="px-2 py-2">
                      <p className="text-sm font-semibold">{user.fullname}</p>
                      <p className="text-xs text-muted-foreground">{user.email}</p>
                    </div>
                    <Separator className="my-1" />
                  </>
                )}
                <button
                  className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-accent transition-colors"
                  onClick={() => { setMenuOpen(false); setProfileOpen(true); }}
                >
                  <User className="h-4 w-4" />
                  Profile
                </button>
                <button
                  className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-red-600 hover:text-white transition-colors"
                  onClick={() => { setMenuOpen(false); setLogoutOpen(true); }}
                >
                  <LogOut className="h-4 w-4" />
                  Logout
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <ProfileDialog open={profileOpen} onClose={() => setProfileOpen(false)} />
      <LogoutDialog open={logoutOpen} onClose={() => setLogoutOpen(false)} onConfirm={() => { setLogoutOpen(false); logout(); }} />
    </header>
  );
}
